import { useEffect, useMemo, useRef, useState } from "react";
import useModalFetch from "../../../../hooks/useModalFetch";
import {
  cardStatusFromAppointment,
  extractScheduleDetailAppointments,
} from "../../../../utils/scheduleCardFlags";
import type { ScheduleCardStatus } from "../../../../types/scheduleCard";
import { resolveAppointmentScheduleNote } from "../../../../utils/scheduleNote";

export type ScheduleDetailAppointment = {
  id: number;
  schedule: {
    id: number;
    date: string;
    note?: string | null;
  };
  note?: string | null;
  consultant: {
    first_name: string;
    phone: string;
  };
  client: Record<string, unknown> | null;
  owner: Record<string, unknown> | null;
  property: Record<string, unknown> | null;
  video_call: number;
  outcome?: "completed" | "no_show" | null;
  key_location?: string | null;
  has_card?: boolean;
  card_id?: number | null;
  can_create_card?: boolean;
};

function dedupeAppointments(
  appointments: ScheduleDetailAppointment[]
): ScheduleDetailAppointment[] {
  const seen = new Set<number>();
  return appointments.filter((appointment) => {
    const key = appointment.schedule.id;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function useScheduleDetailAppointments(params: {
  open: boolean;
  scheduleParam: string;
  targetScheduleId?: number;
}) {
  const { open, scheduleParam, targetScheduleId } = params;
  const endpoint = scheduleParam
    ? `/api/adm/schedules/${encodeURIComponent(scheduleParam)}`
    : "";

  const { fetchedData, isFetching } = useModalFetch(open ? endpoint : "");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardStatusOverride, setCardStatusOverride] =
    useState<ScheduleCardStatus | null>(null);
  const lastSyncedRef = useRef("");

  const appointments = useMemo(() => {
    const raw = extractScheduleDetailAppointments(fetchedData);
    return dedupeAppointments(raw as ScheduleDetailAppointment[]);
  }, [fetchedData]);

  const currentAppointment = appointments[currentIndex] ?? null;
  const currentScheduleId = currentAppointment?.schedule.id;

  useEffect(() => {
    setCardStatusOverride(null);
  }, [currentScheduleId]);

  useEffect(() => {
    if (!open) {
      lastSyncedRef.current = "";
      return;
    }
    if (appointments.length === 0) {
      return;
    }

    const signature = `${scheduleParam}|${targetScheduleId ?? ""}|${appointments
      .map((item) => item.schedule.id)
      .join(",")}`;

    if (lastSyncedRef.current === signature) {
      return;
    }
    lastSyncedRef.current = signature;

    if (targetScheduleId != null) {
      const targetIndex = appointments.findIndex(
        (item) => item.schedule.id === targetScheduleId
      );
      if (targetIndex >= 0) {
        setCurrentIndex(targetIndex);
        return;
      }
    }
    setCurrentIndex(0);
  }, [open, appointments, scheduleParam, targetScheduleId]);

  const cardStatus =
    cardStatusOverride ??
    (currentAppointment
      ? cardStatusFromAppointment(currentAppointment)
      : { has_card: false, card_id: null, can_create_card: false });

  const savedNote = currentAppointment
    ? resolveAppointmentScheduleNote(currentAppointment)
    : "";

  return {
    appointments,
    currentAppointment,
    currentIndex,
    setCurrentIndex,
    isFetching,
    cardStatus,
    setCardStatusOverride,
    savedNote,
  };
}
